import React from "react";
import moment from "moment-timezone";
import { makeStyles } from "@material-ui/core/styles";
import { Popover, IconButton } from "@material-ui/core";
import { Pencil, CalendarClock, MessageSquare, User, Send, Ticket } from "lucide-react";
import { CloseXIcon } from "../ChatiaIcons";
import BaseActionButton from "../ActionButtons";
import { i18n } from "../../translate/i18n";

const WEEKDAYS_LONG = ["domingo", "segunda-feira", "terça-feira", "quarta-feira", "quinta-feira", "sexta-feira", "sábado"];
const MONTHS_LONG = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];

const TrashSvg = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M19.5 5.5L18.8803 15.5251C18.7219 18.0864 18.6428 19.3671 18.0008 20.2879C17.6833 20.7431 17.2747 21.1273 16.8007 21.416C15.8421 22 14.559 22 11.9927 22C9.42312 22 8.1383 22 7.17905 21.4149C6.7048 21.1257 6.296 20.7408 5.97868 20.2848C5.33688 19.3626 5.25945 18.0801 5.10461 15.5152L4.5 5.5" />
    <path d="M3 5.5H21M16.0557 5.5L15.3731 4.09173C14.9196 3.15626 14.6928 2.68852 14.3017 2.39681C14.215 2.3321 14.1231 2.27454 14.027 2.2247C13.5939 2 13.0741 2 12.0345 2C10.9688 2 10.436 2 9.99568 2.23412C9.8981 2.28601 9.80498 2.3459 9.71729 2.41317C9.32164 2.7167 9.10063 3.20155 8.65861 4.17126L8.05292 5.5" />
    <path d="M9.5 16.5L9.5 10.5" />
    <path d="M14.5 16.5L14.5 10.5" />
  </svg>
);

const safeT = (key, fallback) => {
  const out = i18n.t(key);
  return !out || out === key ? fallback : out;
};

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark" || theme.mode === "dark";
  const textPrimary = isDark ? "#e9edef" : "#111b21";
  const textSecondary = isDark ? "#8696a0" : "#5b6470";
  const textMuted = isDark ? "#566069" : "#94a3b8";
  return ({
    paper: {
      borderRadius: "21px 21px 12px 12px",
      width: 360,
      maxWidth: "95vw",
      maxHeight: "85vh",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      boxShadow: isDark
        ? "0 8px 24px rgba(0,0,0,0.45)"
        : "0 4px 20px rgba(15,23,42,0.10)",
      border: isDark
        ? "1px solid rgba(255,255,255,0.06)"
        : "1px solid rgba(0,0,0,0.06)",
      background: isDark ? "#121212" : "#ffffff",
    },
    header: {
      ...theme.chatiaDS.header,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexShrink: 0,
    },
    headerTitle: {
      ...theme.chatiaDS.headerTitle,
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
    headerActions: {
      display: "flex",
      alignItems: "center",
      gap: 4,
    },
    headerIconBtn: {
      backgroundColor: "rgba(255, 255, 255, 0.10)",
      color: "#ffffff",
      transition: "all 0.2s ease",
      padding: 6,
      "&:hover": {
        backgroundColor: "rgba(255, 255, 255, 0.22)",
        color: "#ffffff",
      },
    },
    content: {
      padding: "16px 18px",
      background: isDark ? "#121212" : "#ffffff",
      ...theme.scrollbarStylesSoft,
      flex: 1,
      minHeight: 0,
      overflowY: "auto",
    },
    badge: {
      display: "inline-block",
      fontSize: 11,
      fontWeight: 600,
      padding: "2px 10px",
      borderRadius: 99,
      marginBottom: 10,
      background: "rgba(249, 115, 22, 0.12)",
      color: "#f97316",
    },
    title: {
      fontSize: 18,
      fontWeight: 700,
      color: textPrimary,
      marginBottom: 4,
      lineHeight: 1.3,
    },
    description: {
      fontSize: 13,
      color: textSecondary,
      marginBottom: 16,
      lineHeight: 1.45,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
    },
    detailRow: {
      display: "flex",
      alignItems: "flex-start",
      gap: 10,
      padding: "6px 0",
      color: textPrimary,
    },
    detailIcon: {
      color: textSecondary,
      flexShrink: 0,
      marginTop: 1,
      display: "inline-flex",
    },
    detailText: {
      fontSize: 13,
      fontWeight: 500,
      lineHeight: 1.4,
      color: textPrimary,
    },
    detailSub: {
      fontSize: 12,
      color: textSecondary,
      fontWeight: 400,
    },
    divider: {
      height: 1,
      background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
      margin: "12px 0",
    },
    createdBy: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: 12,
      color: textMuted,
      padding: "4px 0",
    },
    footer: {
      flexShrink: 0,
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      gap: 8,
      padding: "10px 14px",
      borderTop: isDark
        ? "1px solid rgba(255,255,255,0.08)"
        : "1px solid rgba(0,0,0,0.08)",
      background: isDark ? "#121212" : "#ffffff",
    },
  });
});

const ScheduleDetailPopover = ({ open, anchorEl, side = "right", schedule, timezone, onClose, onEdit, onDelete }) => {
  const classes = useStyles();

  if (!schedule) return null;

  const scheduledAt = schedule.sendAt
    ? moment.utc(schedule.sendAt).tz(timezone || "America/Sao_Paulo")
    : null;

  const formatFullDate = () => {
    if (!scheduledAt || !scheduledAt.isValid()) return "";
    const dt = scheduledAt.toDate();
    return `${WEEKDAYS_LONG[dt.getDay()]}, ${String(dt.getDate()).padStart(2, "0")} de ${MONTHS_LONG[dt.getMonth()]} de ${dt.getFullYear()}`;
  };

  const statusLabel = schedule.sentAt
    ? safeT("schedules.status.sent", "Enviada")
    : schedule.status === "ERRO"
      ? safeT("schedules.status.error", "Erro no envio")
      : safeT("schedules.status.scheduled", "Programada");

  const channel = schedule.whatsapp?.channel || schedule.contact?.channel || "WhatsApp";
  const contactName = schedule.contact?.name || safeT("scheduleModal.form.contact", "Contato");
  const ticketStatus = schedule.openTicket === "enabled"
    ? (schedule.statusTicket === "open" ? safeT("campaigns.dialog.form.openTicketStatus", "Aberto") : safeT("campaigns.dialog.form.closedTicketStatus", "Fechado"))
    : safeT("campaigns.dialog.form.disabled", "Desabilitado");

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "top", horizontal: side === "left" ? "left" : "right" }}
      transformOrigin={{ vertical: "top", horizontal: side === "left" ? "right" : "left" }}
      PaperProps={{ className: classes.paper }}
    >
      <div className={`${classes.header} chatia-ds-header`}>
        <div className={classes.headerTitle}>
          {safeT("schedules.detail.title", "Mensagem agendada")}
        </div>
        <div className={classes.headerActions}>
          <IconButton size="small" onClick={onClose} className={classes.headerIconBtn}>
            <CloseXIcon style={{ fontSize: 18 }} />
          </IconButton>
        </div>
      </div>

      <div className={classes.content}>
        <span className={classes.badge}>{statusLabel}</span>
        <div className={classes.title}>{contactName}</div>

        {schedule.body && (
          <div className={classes.description}>{schedule.body}</div>
        )}

        <div className={classes.divider} />

        <div className={classes.detailRow}>
          <span className={classes.detailIcon}><CalendarClock size={16} strokeWidth={1.6} /></span>
          <div>
            <div className={classes.detailText}>{formatFullDate()}</div>
            <div className={classes.detailSub}>{scheduledAt?.isValid() ? scheduledAt.format("HH:mm") : ""}</div>
          </div>
        </div>

        <div className={classes.detailRow}>
          <span className={classes.detailIcon}><Send size={16} strokeWidth={1.6} /></span>
          <div>
            <div className={classes.detailText}>{channel}</div>
            {schedule.whatsapp?.name && <div className={classes.detailSub}>{schedule.whatsapp.name}</div>}
          </div>
        </div>

        <div className={classes.detailRow}>
          <span className={classes.detailIcon}><Ticket size={16} strokeWidth={1.6} /></span>
          <div>
            <div className={classes.detailText}>{safeT("campaigns.dialog.form.openTicket", "Abrir ticket")}: {ticketStatus}</div>
            {schedule.ticketUser?.name && <div className={classes.detailSub}>{schedule.ticketUser.name}</div>}
          </div>
        </div>

        {schedule.mediaName && (
          <div className={classes.detailRow}>
            <span className={classes.detailIcon}><MessageSquare size={16} strokeWidth={1.6} /></span>
            <div className={classes.detailText}>{schedule.mediaName}</div>
          </div>
        )}

        <div className={classes.divider} />

        {schedule.user && (
          <div className={classes.createdBy}>
            <User size={16} strokeWidth={1.6} />
            {safeT("eventDetailPopover.createdBy", "Criado por")}: {schedule.user.name}
          </div>
        )}
      </div>

      <div className={classes.footer}>
        <BaseActionButton
          tone="danger"
          onClick={() => { onDelete(schedule); onClose(); }}
          iconSvg={<TrashSvg />}
          style={{ minWidth: 112 }}
        >
          {safeT("eventDetailPopover.delete", "Excluir")}
        </BaseActionButton>
        <BaseActionButton
          onClick={() => { onEdit(schedule); onClose(); }}
          iconSvg={<Pencil size={14} strokeWidth={2} />}
          style={{ minWidth: 112 }}
        >
          {safeT("eventDetailPopover.edit", "Editar")}
        </BaseActionButton>
      </div>
    </Popover>
  );
};

export default ScheduleDetailPopover;
